import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
  } from 'react';
  import { useTranslation } from 'react-i18next';
  import {
 DialedDigitExtractionServerUpdateCallback, FormTemplate, FormTemplateSection, FormTemplateValidator
} from 'data/template/template.types';
  import * as types from 'data/filters/filters.types';
  import Form, { CustomValidationFunction, FieldUpdateHandlerParam } from 'shared/form';
  import './filters-form.scss';
  import { FormLayout } from 'components/FormLayout';
import { TranslationKey } from 'i18n/i18n.types';
import getExistingAccessFunctionNames from 'routes/AccessFunction/utils/getExistingAccessFunctionNames';
import { validateNonRepeatedProperty } from 'utils/validators/listValidators';
import ipRegex from 'ip-regex';
import IntervalValidator from 'shared/form/validators/intervalValidator';

  type Props = {
    template: FormTemplate,
    data: Partial<types.Filters>
    i18nKeyPrefix?: string
    onFieldUpdate?: (param: FieldUpdateHandlerParam, formSection?: 'main') => void
    onFieldCreateNew?: DialedDigitExtractionServerUpdateCallback
  }

  export type Ref = {
    isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
    getValues: () => types.Filters
  }

  export const FILTERS_I18N_KEY = 'filters.form';

  export const FiltersForm = forwardRef<Ref, Props>(({
    template,
    data,
    i18nKeyPrefix = FILTERS_I18N_KEY,
    onFieldUpdate = () => null,
    onFieldCreateNew
  }: Props, ref) => {
    const mainRef = useRef<Form>(null);
    const { t } = useTranslation();

    const isValid = (forceValidation = false, forceTouched = true) => {
      const isMainValid = mainRef.current?.isValid(forceValidation, forceTouched) ?? true;
      return isMainValid;
    };

    const getValues = () => {
      const formValues: types.Filters = {
        ...mainRef.current?.getValues(),
      };

      return formValues;
    };

    useImperativeHandle(ref, () => ({
      isValid,
      getValues,
    }));

    const ipPortValidator = useCallback<CustomValidationFunction>((
      _propFields?: unknown,
      _stateFields?: unknown,
      _value?: string,
      _lastSavedValue?: string,
      validator?: FormTemplateValidator,
      form?: Form | undefined,
    ) => {
      const validationResult = { isValid: true, errorMessage: '' };
      const ipPortValue = form?.getValues()?.value;
      if (ipPortValue) {
        const ipPort = ipPortValue.split(':');
        if (ipPort.length !== 2 && validator?.customValidationFunction) {
          validationResult.isValid = false;
          validationResult.errorMessage = 'Invalid \'IPv4 address and Port\' format. Valid format examples: \'1.2.3.4:1234\'' ?? '';
        }

        if (validationResult.isValid && !ipRegex({ exact: true }).test(ipPort[0])) {
          validationResult.isValid = false;
          validationResult.errorMessage = 'Invalid IPv4 address.';
        }

        const port = ipPort[1];
        if (!((typeof port === 'string' || typeof port === 'number')
              && port >= 0 && port <= 65535)) {
                validationResult.isValid = false;
                validationResult.errorMessage += 'Invalid port number.';
        }
      }

      return validationResult;
    }, [i18nKeyPrefix, t]);

    const ipv6Validator = useCallback<CustomValidationFunction>((
      _propFields?: unknown,
      _stateFields?: unknown,
      _value?: string,
      _lastSavedValue?: string,
      validator?: FormTemplateValidator,
      form?: Form | undefined,
    ) => {
      const validationResult = { isValid: true, errorMessage: '' };
      let value = _value;
      if (value === undefined || value.trim().length === 0) {
        return validationResult;
      }
      const ipValue = value
      if (ipValue.indexOf('[') >= 0 || ipValue.indexOf(']') > 0) {
        validationResult.isValid = false;
        validationResult.errorMessage = 'Invalid IPv6 address.';
        return validationResult;
      }

      // ipv6 has subnet
      if (value.indexOf('/') >=0) {
        const parts = value.split('/');
        if (parts.length > 2) {
          validationResult.isValid = false;
          validationResult.errorMessage = 'Invalid IPv6 address.';
          return validationResult;
        }
        try {
          const subnet = parseInt(parts[1], 10);
          // eslint-disable-next-line prefer-destructuring
          value = parts[0];

          // check and erase the [] bracket
          try {
            if (value.indexOf('[') === 0 && value.indexOf(']') > 0) {
              value = value.substring(1, value.length - 1);
            }
          } catch (err) {
            validationResult.isValid = false;
            validationResult.errorMessage = 'Invalid IPv6 address.';
            return validationResult;
          }

          const isIPv6 = (value.indexOf(':') >= 0);
          if (isIPv6 && !(subnet >= 0 && subnet <= 255)) {
            validationResult.isValid = false;
            validationResult.errorMessage = 'Invalid IPv6 address.';
            return validationResult;
          }
        } catch (err) {
          validationResult.isValid = false;
          validationResult.errorMessage = 'Invalid IPv6 address.';
          return validationResult;
        }
      }

      const toks = value.split('.');
      const len = toks.length;
      let sb = '';
      for (let i =0; i < len; i+=1) {
        // skip the '0' case
        if (toks[i].length === 1) {
          sb = sb.concat(toks[i]);
          if (i < len-1) { sb = sb.concat('.'); }
          /* eslint-disable no-continue */
          continue;
        }

        // if s has leading zero, trim it
        while (toks[i].charAt(0) === '0') {
          // strip out the zero(s)
          toks[i] = toks[i].substring(1);
        }

        sb = sb.concat(toks[i]);
        if (i < len-1) { sb = sb.concat('.'); }
      }

      value = sb.toString();
      // Check if the value matches the regular expression
      if (!(isValidIPv6Address(value) || isValidIPV4Compatible(value) || containsAllowedSpecialValues(value))
          || containsInvalidIP(value)) {
          validationResult.isValid = false;
          validationResult.errorMessage = 'Invalid IPv6 address.';
          return validationResult;
      }

      return validationResult;
    }, [i18nKeyPrefix, t]);

    const containsInvalidIP = (aValue: string) => {
      const iInvalidValues = ['0.0.0.0', '255.255.255.255'];
      if (iInvalidValues == null) {
        return false;
      }
      if (iInvalidValues.includes(aValue)) {
        return true;
      }
      return false;
    }

    const containsAllowedSpecialValues = (aValue: string) => {
      const allowedSpecialValues = ['ADDR_ANY', 'Any'];
      if (allowedSpecialValues == null) {
        return false;
      }

      if (allowedSpecialValues.includes(aValue)) {
        return true;
      }
      return false;
    }

    const isValidIPV4Compatible = (ipaddress: string) => {
      let pv4=0; let pv6=0;
      const pm = ipaddress.lastIndexOf(':');
      const pm2 = ipaddress.lastIndexOf('::');
      if (pm >= 0){
        pv4 = pm+1;
        if (pm2+1 === pm){
          pv6 = pm+1;
        } else {
          pv6 = pm;
        }
        return isValidIPv6Address(ipaddress.substring(0, pv6)) && isValidIPv4Address(ipaddress.substring(pv4));
      }
      return false;
    }

    const isValidIPv4Address = (ipaddress: string) => {
      return ipRegex({ exact: true }).test(ipaddress);
    }

    const isValidIPv6Address = (ipaddress: string) => {
      const hex = '([0]*)([0-9a-fA-F]{1,4})';

      // RFC5952: The '::' can only appear once in an address.
      const indexOfDoubleColons = ipaddress.indexOf('::');
      if (indexOfDoubleColons >= 0 && ipaddress.indexOf('::', indexOfDoubleColons+2) > 0) {
        return false;
      }

      // three or more consecutive colons
      if (new RegExp('(.*):::([:]*)(.*)').test(ipaddress)) {
        return false;
      }

      if (ipaddress.startsWith('::')){
        ipaddress = ipaddress.substring(2);
      }
      if (ipaddress.endsWith('::')){
        ipaddress = ipaddress.substring(0, ipaddress.length-2);
      }
      // make sure each part has a valid value
      let previousEmpty = false;
      const parts = ipaddress.split(':');
      for (let i=0; i < parts.length; i+=1) {
        if (parts[i].length > 0) {
          previousEmpty = false;
          if (new RegExp(hex).test(parts[i]) === false) {
            return false;
          }
        } else if (parts[i].length === 0) {
          if (previousEmpty === true) {
            return false;
          }
            previousEmpty = true;

        }
      }
      if (parts.length === 9 && new RegExp('(.*)::(.*)').test(ipaddress) === false) {
        return false;
      }
      if (parts.length > 8) {
        return false;
      }
      if (indexOfDoubleColons < 0 && parts.length < 8) {
        return false;
      }
      return true;

    }

    const ipv6PortValidator = useCallback<CustomValidationFunction>((
      _propFields?: unknown,
      _stateFields?: unknown,
      _value?: string,
      _lastSavedValue?: string,
      validator?: FormTemplateValidator,
      form?: Form | undefined,
    ) => {
      const validationResult = { isValid: true, errorMessage: '' };
      const ipPortValue = form?.getValues()?.value;
      const splitIndex = ipPortValue.lastIndexOf(':');
      if (splitIndex < 0){
        validationResult.isValid = false;
        validationResult.errorMessage = 'Invalid \'IPv6 address and Port\' format. Valid format examples: \'[2080::221:28ff:fe00:d8d8]:1234\'';
        return validationResult;
      }
      let ipValue = '';
      let portValue = '';
      try {
        ipValue = ipPortValue.substring(0, splitIndex);
        // erase [] if the format is [::/12]
        // skip the scenario if the format is [::]/12, IPv6AddressFieldValidator can handle it.
        if (ipValue.indexOf('[') === 0 && ipValue.indexOf(']') === (ipValue.length - 1)) {
          ipValue = ipValue.substring(1, ipValue.length - 1);
        }
        portValue = ipPortValue.substring(splitIndex+1);
      } catch (err) {
        validationResult.isValid = false;
        validationResult.errorMessage = 'Invalid \'IPv6 address and Port\' format. Valid format examples: \'[2080::221:28ff:fe00:d8d8]:1234\'';
        return validationResult;
      }
      const { isValid, errorMessage } = ipv6Validator(
        _propFields = {},
        _stateFields = {},
        ipValue,
        _lastSavedValue = '',
        validator,
        form,
      );

      if (!isValid){
        validationResult.isValid = false;
        validationResult.errorMessage = 'Invalid IPv6 address.';
        return validationResult;
      }
      if (!isValidPort(portValue)){
        validationResult.isValid = false;
        validationResult.errorMessage = 'Invalid port number.';
        return validationResult;
      }

      return validationResult;
    }, [i18nKeyPrefix, t]);

    const juniperAddressValidator = useCallback<CustomValidationFunction>((
      _propFields?: unknown,
      _stateFields?: unknown,
      _value?: string,
      _lastSavedValue?: string,
      validator?: FormTemplateValidator,
      form?: Form | undefined,
    ) => {
      const validationResult = { isValid: true, errorMessage: '' };
      const value = form?.getValues()?.value;
      if (value === undefined || value.trim().length === 0) {
        return validationResult;
      }
      if (value.toUpperCase() === 'ANY') {
        return validationResult;
      }
      if (value.toUpperCase().startsWith('HOST ')) {
        const tempstr = value.split(' ');
        if (tempstr.length !== 2) {
          validationResult.isValid = false;
        } else {
          const ip = tempstr[1];
          validationResult.isValid = ipRegex({ exact: true }).test(ip);
        }
      } else {
        const tempstr = value.split(' ');
        if (tempstr.length !== 2) {
          validationResult.isValid = false;
        } else {
          const ip1 = tempstr[0];
          const ip2 = tempstr[1];
          validationResult.isValid = ipRegex({ exact: true }).test(ip1) && ipRegex({ exact: true }).test(ip2);
          if (validationResult.isValid) {
            validationResult.isValid=wildmaskverify(ip2);
          }

        }
      }
      return validationResult;
    }, [i18nKeyPrefix, t]);

    const wildmaskverify = (wildipmask: any) => {
      const ippart = wildipmask.split('.');
      if (ippart.length !== 4) { return false; }
      let binaryrep = '';
      for (let i = 0; i < 4; i+=1) {
        binaryrep += convertBinary(parseInt(ippart[i], 10));

      }
      return binaryrep.indexOf('10') < 0;
    }

    const convertBinary = (sum: number) => {
      if (sum === 0) return '00000000';
      if (sum === 1) return '00000001';
      let binary = '';
      while (sum !== 0 && sum !== 1) {
        binary = (sum % 2) + binary;
        sum /= 2;
        if (sum === 0 || sum === 1) {
          binary = (sum % 2) + binary;
        }
      }
      const len = 8;
      const binaryzerostr = '00000000';
      let ret = binary.toString();
      const lacklen = len - binary.toString().length;
      if (lacklen > 0) {
        ret = binaryzerostr.substring(8 - lacklen) + ret;

      }
      return ret;
    }

    const juniperQualifierValidator = useCallback<CustomValidationFunction>((
      _propFields?: unknown,
      _stateFields?: unknown,
      _value?: string,
      _lastSavedValue?: string,
      validator?: FormTemplateValidator,
      form?: Form | undefined,
    ) => {
      const validationResult = { isValid: true, errorMessage: '' };
      const value = form?.getValues()?.value;
      if (value === undefined || value.trim().length === 0) {
        return validationResult;
      }
      const valueUpper = value.toUpperCase();
      if (valueUpper.startsWith('LT ') || valueUpper.startsWith('GT ')
      || valueUpper.startsWith('EQ ') || valueUpper.startsWith('NE ')
      ||valueUpper.startsWith('RANGE ')) {
        const tempstr = value.split(' ');
        if (tempstr.length === 2) {
          const port = tempstr[1];
          validationResult.isValid = isValidPort(port);
        } else if (tempstr.length === 3) {
          const port1 = tempstr[1];
          const port2 = tempstr[2];
          validationResult.isValid = isValidPort(port1) && isValidPort(port2);
        } else {
          validationResult.isValid = false;
        }
      } else {
        validationResult.isValid = false;
      }
      return validationResult;
    }, [i18nKeyPrefix, t]);

    const isValidPort = (strport: any): boolean => {
      try {
        const port = parseInt(strport, 10);
        return (port >= 1 && port <= 65535);
      } catch (err) {
        return false;
      }
    }

    const customValidationFunctionMap = {
      filtersIpPortValidator: ipPortValidator,
      addressValidator: juniperAddressValidator,
      qualifierValidator: juniperQualifierValidator,
      filtersIpv6Validator: ipv6Validator,
      filtersIpv6PortValidator: ipv6PortValidator,
    };

    const onMainFieldUpdate = (param: FieldUpdateHandlerParam) => onFieldUpdate(param, 'main');

    return (
      <div className="filters-form" data-testid="FiltersForm">
        <div data-testid="FiltersFormMain">
          <FormLayout>
            <Form
              ref={mainRef}
              name={template.formId ?? template.key}
              defaults={data}
              fields={(template.main as FormTemplateSection).fields}
              layout={(template.main as FormTemplateSection).metadata.layout?.rows}
              i18nKeyPrefix={i18nKeyPrefix}
              fieldUpdateHandler={onMainFieldUpdate}
              shouldUpdateRows
              fieldCreateNewHandler={onFieldCreateNew}
              customValidationFunctionMap={customValidationFunctionMap}
            />
          </FormLayout>
        </div>
      </div>
    );
  });

  export default FiltersForm;
